import request from 'lib/request';
import type {
  DocumentInterpretationResponse,
  DocumentResponse,
  Point,
  WellboreMapData,
} from 'types/api-responses';

const toSentence = (indices: number[]) => {
  const indicesCopy = [...indices];

  const last = indicesCopy.pop();
  if (indicesCopy.length) {
    return `${indicesCopy.join(', ')} and ${last}`;
  } else {
    return last;
  }
};

const removeReferee = (href: string) => {
  return request.deleteReq<null, null | { errors: { base: string[] } }>(
    href,
    undefined,
    new AbortController()
  );
};

const markAsDuplicate = (href: string) => {
  return request.patch(href, { duplicate: true }, new AbortController());
};

const getInterpretationIndices = (
  interpretations: DocumentInterpretationResponse[],
  reference: 'grantor' | 'grantee' | 'document' | 'location',
  id: number
) => {
  return interpretations.reduce<number[]>((acc, item, index) => {
    const references = item[`${reference}References`]?.some((referer) => {
      if ('grantorId' in referer) {
        return referer.grantorId === id;
      }

      if ('granteeId' in referer) {
        return referer.granteeId === id;
      }

      if ('location' in referer) {
        return referer.location.id === id;
      }

      return referer.document.id === id;
    });

    if (references) {
      acc.push(index + 1);
    }
    return acc;
  }, []);
};

const ERROR_TEXT = 'Something went wrong.';

type ErrorResponseBody = {
  type: string;
  data?: {
    errors?: {
      base: string[];
    };
  } | null;
  message?: string;
};

const extractErrorMessage = (
  response: ErrorResponseBody,
  defaultErrorMessage = ERROR_TEXT
): string => {
  if (response.type === 'error' && response.data?.errors?.base) {
    return response.data.errors.base[0];
  }
  return defaultErrorMessage;
};

const resetRowSelectionAndError = (
  setSelectedRowIndex: React.Dispatch<React.SetStateAction<number>>,
  setError: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  setSelectedRowIndex(-1);
  setError(undefined);
};

/**
 * The distinct UWIs of the wells matched to an AFE document's targets; empty
 * for every other document type.
 * @param {DocumentResponse} document - The document whose AFE targets to read
 * @return {string[]} The matched wells' UWIs, first occurrence first
 */
const getAfeTargetUwis = (document: DocumentResponse): string[] => {
  if (document.type !== 'Document::Afe') return [];
  return [
    ...new Set(
      (document.afeTargets ?? []).flatMap((target) =>
        target.well?.uwi ? [target.well.uwi] : []
      )
    ),
  ];
};

/**
 * Southwest/northeast corners enclosing every wellbore's geometry, in the
 * `[point, point]` shape `useCalculateMapDimensions` fits bounds around.
 * @param {WellboreMapData[]} wellbores - Wellbores whose geometry to enclose
 * @return {[Point, Point] | null} Bounds, or null when no wellbore carries geometry
 */
const getWellboreBounds = (
  wellbores: WellboreMapData[]
): [Point, Point] | null => {
  const coordinates = wellbores.flatMap((wellbore) => {
    const points: Point[] = [];
    if (wellbore.surfaceHole) {
      points.push(wellbore.surfaceHole.coordinates as Point);
    }
    if (wellbore.bottomHole) {
      points.push(wellbore.bottomHole.coordinates as Point);
    }
    // Stick/survey lines cover wellbores whose response has no hole points.
    for (const feature of [wellbore.stick, wellbore.survey]) {
      const geometry = feature?.geometry;
      if (geometry?.type === 'LineString') {
        points.push(...(geometry.coordinates as Point[]));
      }
    }
    return points;
  });
  if (!coordinates.length) return null;

  const longitudes = coordinates.map((coordinate) => coordinate[0]);
  const latitudes = coordinates.map((coordinate) => coordinate[1]);
  return [
    [Math.min(...longitudes), Math.min(...latitudes)],
    [Math.max(...longitudes), Math.max(...latitudes)],
  ];
};

/**
 * One map layer per AFE wellbore. Its id names the well and its position, so
 * two wellbores of one well (or with no UWI) never share a layer id.
 * @param {WellboreMapData[]} wellbores - The AFE's matched wellbores
 * @return {{ id: string, wellbore: WellboreMapData }[]} The layers, in order
 */
const getAfeWellLayers = (
  wellbores: WellboreMapData[]
): { id: string; wellbore: WellboreMapData }[] =>
  wellbores.map((wellbore, index) => ({
    id: `afe-well-${wellbore.uwi14 ?? 'unknown'}-${index}`,
    wellbore,
  }));

export {
  getAfeTargetUwis,
  getAfeWellLayers,
  getWellboreBounds,
  getInterpretationIndices,
  markAsDuplicate,
  removeReferee,
  toSentence,
  ERROR_TEXT,
  extractErrorMessage,
  resetRowSelectionAndError,
};
